import { useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getPlanLimits } from '../utils/planConfig';
import {
  getExceededLimits,
  getResourceLimitMessage,
  isAtLimit,
  ResourceUsage,
  UsageResource,
} from '../utils/planUsage';
import { useBillingStatus } from './useBilling';

export function usePlanUsage() {
  const { organization } = useAuth();
  const { data: billing, isLoading } = useBillingStatus();

  const limits = useMemo(
    () => getPlanLimits(organization?.plan || 'free', organization?.trialEndsAt),
    [organization?.plan, organization?.trialEndsAt]
  );

  const usage = useMemo(
    () =>
      billing
        ? {
            users: billing.activeUsers,
            projects: billing.projectCount ?? 0,
            clients: billing.clientCount ?? 0,
          }
        : null,
    [billing]
  );

  const exceededLimits: ResourceUsage[] = useMemo(() => {
    if (!usage) return [];
    return getExceededLimits(limits, usage);
  }, [usage, limits]);

  const checkIsAtLimit = useCallback(
    (resource: UsageResource) => {
      if (!usage) return false;
      return isAtLimit(limits, usage, resource);
    },
    [limits, usage]
  );

  const getLimitMessage = useCallback(
    (resource: UsageResource) => {
      if (!usage) return '';
      return getResourceLimitMessage(limits, usage, resource);
    },
    [limits, usage]
  );

  return {
    isLoading,
    limits,
    exceededLimits,
    hasReachedLimit: exceededLimits.length > 0,
    usage,
    isAtLimit: checkIsAtLimit,
    getResourceLimitMessage: getLimitMessage,
  };
}
